import React, { useEffect, useMemo, useState } from "react";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";

import { useSelector } from "react-redux";
import { RootState } from "@/redux/store";
import { useToast } from "@/components/ui/use-toast";
import { Controller, FormProvider, useForm } from "react-hook-form";
import { Button } from "@/components/ui/button";
import { LoadingButton } from "@/components/ui/common/loading-button";
import { Separator } from "@/components/ui/separator";

import { ErrorType } from "@/types";
import { InvoiceStatus, InvoiceType, TaxType } from "@/types/enums";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ChevronDown } from "lucide-react";
import { useUpdateSystemSettingsMutation } from "@/services/systemConfigApi";
import { SystemSettings } from "@/types/system-setting/system-config";
import { useGetTaxGroupQuery } from "@/services/salesTaxApi";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { TaxGroup } from "@/types/system-setting/tax-group";
interface OtherSettingsProps {
  isOpen: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  action: string;
  setAction: React.Dispatch<React.SetStateAction<"add" | "edit">>;
  refetch: any;
  editModeData: any | undefined;
}
const OtherSettings = ({
  isOpen,
  setOpen,
  action,
  setAction,
  editModeData,
  refetch,
}: OtherSettingsProps) => {

  const orgId =
    useSelector((state: RootState) => state.auth.userInfo?.user?.org_id) || 0;
  const userInfo = useSelector((state: RootState) => state.auth.userInfo?.user);
  const { toast } = useToast();
  const [taxType, setTaxType] = useState("exclusive");
  const [updateSetting, { isLoading, error, status }] = useUpdateSystemSettingsMutation();

  const { data: taxGroupData } = useGetTaxGroupQuery({ query: "type=member", org_id: orgId }, {
    skip: !isOpen,
  });

  const taxGroupOptions = useMemo(() => {
    return Array.isArray(taxGroupData?.data)
      ? (taxGroupData?.data
        .filter((item: TaxGroup) => item.id !== undefined)
        .map((item: TaxGroup) => ({
          value: item.id as number,
          label: `${item.name}`,
        })))
      : [];
  }, [taxGroupData]);

  const initialValues = {
    group_name: "other_settings",
    tax_type: "exclusive",
    invoice_prefix: "",
    member_id_prefix: "",
    coach_id_prefix: "",
    staff_id_prefix: "",
    grace_period: "",
    srb_tax_prefix: "",
    gst_tax_prefix: "",
    donation_tax_prefix: "",
    other_tax_prefix: "",
    mobile_default_tax_group_id: "",
  };

  const form = useForm<SystemSettings>({
    mode: "all",
    defaultValues: initialValues,
  });

  const {
    control,
    watch,
    register,
    handleSubmit,
    clearErrors,
    getValues,
    reset,
    setValue,
    formState: { isSubmitting, errors },
  } = form;
  const watcher = watch();

  useEffect(() => {
    if (action == "edit") {
      const payload = { ...editModeData, group_name: "other_settings" };
      setTaxType(payload.tax_type);
      reset(payload as SystemSettings);
    } else {
      reset(initialValues);
    }
  }, [action]);

  const onSubmit = async (formData: SystemSettings) => {
    const { group_name, price_master_count, ...payload } = formData
    try {
      const resp = await updateSetting({ other_settings: payload }).unwrap();
      if (!error && (status === "fulfilled" || status == 'uninitialized')) {
        toast({
          variant: "success",
          title: "Invoice updated Successfully",
        });
        refetch();
        handleClose();
        return resp;
      }
    } catch (error) {
      console.error("Error", { error });
      if (error && typeof error === "object" && "data" in error) {
        const typedError = error as ErrorType;
        toast({
          variant: "destructive",
          title: "Error in Submission",
          description: `${typedError.data?.detail || typedError.data?.message}`,
        });
        return false;
      } else {
        toast({
          variant: "destructive",
          title: "Error in Submission",
          description: `Something Went Wrong.`,
        });
        return false;
      }
    }
  };

  const onError = () => {
    toast({
      variant: "destructive",
      description: "Please fill all the mandatory fields",
    });
  };

  const handleClose = () => {
    clearErrors();
    setAction("add");
    reset(
      {},
      {
        keepIsSubmitted: false, // Reset the form's submission state
        keepSubmitCount: false, // Reset the count of submissions
        keepDirtyValues: false, // Clear any dirty values (set to false to reset all fields)
        keepDefaultValues: true, // Keep default values as provided by initialValue
      }
    );

    setOpen(false);
  };

  console.log({ watcher, errors, action });
  return (
    <Sheet open={isOpen} onOpenChange={handleClose}>
      <SheetContent
        hideCloseButton
        className="bg-formbg w-full lg:!max-w-[1100px] p-0 custom-scrollbar h-full "
      >
        <FormProvider {...form}>
          <form
            key={action}
            noValidate
            className="pb-4 "
            onSubmit={handleSubmit(onSubmit, onError)}
          >
            <SheetHeader className="px-6 sticky top-0 z-40 pt-4 bg-white">
              <SheetTitle>
                <div className="flex justify-between flex-col md:flex-row gap-5 items-start bg-white">
                  <div>
                    <p className="font-semibold">System Configuration</p>
                    <div className="text-sm">
                      <span className="text-gray-400 pr-1 font-semibold">
                        System Configuration
                      </span>{" "}
                      <span className="text-gray-400 font-semibold">/</span>
                      <span className="pl-1 text-primary font-semibold ">
                        {action == "add" ? "Create" : "Edit"} Other Settings
                      </span>
                    </div>
                  </div>

                  <div className="flex justify-center space-x-[20px]">
                    <Button
                      type="button"
                      className="w-[100px] text-center flex items-center gap-2 border-primary"
                      variant={"outline"}
                      disabled={isSubmitting}
                      onClick={handleClose}
                    >
                      <i className="fa fa-xmark "></i>
                      Cancel
                    </Button>
                    <LoadingButton
                      loading={isSubmitting}
                      disabled={isSubmitting}
                      type="submit"
                      className="w-[110px] cursor-pointer bg-primary hover:bg-primary flex items-center gap-1"
                    >
                      {!isSubmitting &&
                        <i className="fa-regular fa-floppy-disk text-base px-1"></i>
                      }
                      {action == "add" ? "Save" : "Update"}
                    </LoadingButton>
                  </div>
                </div>
              </SheetTitle>
            </SheetHeader>
            <Separator className=" h-[1px] rounded-full     " />

            <SheetDescription className=" h-full  p-6  ">
              <Card className="w-full ">
                <CardHeader>
                  <CardTitle className="text-base font-medium">
                    Other Settings
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="grid gap-4 sm:grid-cols-4">
                    {/* Tax Type Selection */}
                    <div className="space-y-2">
                      <Label className="text-sm text-zinc-500">Tax Type</Label>
                      <Tabs
                        value={taxType}
                        onValueChange={(value) => {
                          setTaxType(value);
                          setValue("tax_type", value as TaxType);
                        }}
                        className="w-full"
                      >
                        <TabsList className="grid w-full grid-cols-2">
                          <TabsTrigger
                            value="inclusive"
                            className="data-[state=active]:bg-primary data-[state=active]:text-black"
                          >
                            Inclusive
                          </TabsTrigger>
                          <TabsTrigger
                            value="exclusive"
                            className="data-[state=active]:bg-primary data-[state=active]:text-black"
                          >
                            Exclusive
                          </TabsTrigger>
                        </TabsList>
                      </Tabs>
                    </div>
                    {/* Invoice Prefix */}
                    <div className="space-y-2">
                      <Label
                        htmlFor="invoicePrefix"
                        className="text-sm text-zinc-500"
                      >
                        Invoice Prefix
                      </Label>
                      <div className="relative">
                        <Input
                          id="invoicePrefix"
                          placeholder="Enter Invoice Prefix"
                          className="pr-8 placeholder:text-zinc-400"
                          value={watcher.invoice_prefix}
                          {...register("invoice_prefix")}
                        />
                      </div>
                    </div>

                    {/* Member ID Prefix */}
                    <div className="space-y-2">
                      <Label
                        htmlFor="memberPrefix"
                        className="text-sm text-zinc-500"
                      >
                        Member ID Prefix
                      </Label>
                      <div className="relative">
                        <Input
                          id="memberPrefix"
                          defaultValue="0981"
                          className="pr-8"
                          placeholder="Enter Member Prefix"
                          value={watcher.member_id_prefix}
                          {...register("member_id_prefix")}

                        />
                      </div>
                    </div>

                    {/* Coach ID Prefix */}
                    <div className="space-y-2">
                      <Label
                        htmlFor="coachPrefix"
                        className="text-sm text-zinc-500"
                      >
                        Coach ID Prefix
                      </Label>
                      <div className="relative">
                        <Input
                          id="coachPrefix"
                          defaultValue="0981"
                          className="pr-8"
                          placeholder="Enter Coach Prefix"
                          value={watcher.coach_id_prefix}
                          {...register("coach_id_prefix")}
                        />
                      </div>
                    </div>

                    {/* Staff ID Prefix */}
                    <div className="space-y-2">
                      <Label
                        htmlFor="staffPrefix"
                        className="text-sm text-zinc-500"
                      >
                        Staff ID Prefix
                      </Label>
                      <div className="relative">
                        <Input
                          id="staffPrefix"
                          defaultValue="0981"
                          className="pr-8"
                          placeholder="Enter Staff Prefix"
                          value={watcher.staff_id_prefix}
                          {...register("staff_id_prefix")}
                        />
                      </div>
                    </div>

                    {/* SRB Tax Prefix */}
                    <div className="space-y-2">
                      <Label
                        htmlFor="srbTaxPrefix"
                        className="text-sm text-zinc-500"
                      >
                        SRB Tax Prefix
                      </Label>
                      <div className="relative">
                        <Input
                          id="srbTaxPrefix"
                          className="pr-8"
                          placeholder="Enter SRB Tax Prefix"
                          value={watcher.srb_tax_prefix}
                          {...register("srb_tax_prefix")}
                        />
                      </div>
                    </div>

                    {/* GST Tax Prefix */}
                    <div className="space-y-2">
                      <Label
                        htmlFor="gstTaxPrefix"
                        className="text-sm text-zinc-500"
                      >
                        GST Tax Prefix
                      </Label>
                      <div className="relative">
                        <Input
                          id="gstTaxPrefix"
                          className="pr-8"
                          placeholder="Enter GST Tax Prefix"
                          value={watcher.gst_tax_prefix}
                          {...register("gst_tax_prefix")}
                        />
                      </div>
                    </div>

                    {/* Donation Tax Prefix */}
                    <div className="space-y-2">
                      <Label
                        htmlFor="donationTaxPrefix"
                        className="text-sm text-zinc-500"
                      >
                        Donation Tax Prefix
                      </Label>
                      <div className="relative">
                        <Input
                          id="donationTaxPrefix"
                          className="pr-8"
                          placeholder="Enter Donation Tax Prefix"
                          value={watcher.donation_tax_prefix}
                          {...register("donation_tax_prefix")}
                        />
                      </div>
                    </div>

                    {/* Other Tax Prefix */}
                    <div className="space-y-2">
                      <Label
                        htmlFor="otherTaxPrefix"
                        className="text-sm text-zinc-500"
                      >
                        Other Tax Prefix
                      </Label>
                      <div className="relative">
                        <Input
                          id="otherTaxPrefix"
                          className="pr-8"
                          placeholder="Enter Other Tax Prefix"
                          value={watcher.other_tax_prefix}
                          {...register("other_tax_prefix")}
                        />
                      </div>
                    </div>

                    {/* Grace Period */}
                    <div className="space-y-2">
                      <Label
                        htmlFor="gracePeriod"
                        className="text-sm text-zinc-500"
                      >
                        Grace Period
                      </Label>
                      <div className="relative">
                        <Input
                          id="gracePeriod"
                          defaultValue="3 Days"
                          className="pr-8"
                          placeholder="Enter Grace Period"
                          value={watcher.grace_period}
                          {...register("grace_period", {
                            required: "Required",
                            pattern: {
                              value: /^[0-9]+$/,
                              message: "Please enter a valid number",
                            },

                            setValueAs: (value) => value.toString(),
                          })}
                          type="number"
                        />
                        {errors.grace_period && (
                          <p className="text-red-500 text-sm mt-[5px]">
                            {errors.grace_period.message}
                          </p>
                        )}
                      </div>
                    </div>

                    {/* Mobile Default Tax Dropdown */}
                    <div className="space-y-2">
                      <Label
                        htmlFor="mobileDefaultTax"
                        className="text-sm text-zinc-500"
                      >
                        Mobile Default Tax Group
                      </Label>
                      <Controller
                        name="mobile_default_tax_group_id"
                        control={control}
                        render={({ field }) => (
                          <Select
                            value={field.value?.toString() || ""}
                            onValueChange={(val) => field.onChange(val)}
                          >
                            <SelectTrigger className="w-full">
                              <SelectValue placeholder="Select Default Tax Group" />
                            </SelectTrigger>
                            <SelectContent>
                              {taxGroupOptions.map((opt) => (
                                <SelectItem
                                  key={opt.value}
                                  value={opt.value.toString()}
                                >
                                  {opt.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        )}
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </SheetDescription>
          </form>
        </FormProvider>
      </SheetContent>
    </Sheet>
  );
};

export default OtherSettings;
